import React, { useMemo, useState } from "react";
import { MessageCircle, ArrowUpDown, ChevronUp } from "lucide-react";
import { BlockPreview } from "modules/swiftboard/knowledge-management/knowledge-configuration/knowledge-article/article-builder/BlockCard";
import { BACKEND_TYPE_TO_BLOCK } from "modules/swiftboard/knowledge-management/knowledge-configuration/knowledge-article/article-builder/blocks.data";
import LikeButton from "./LikeButton";
import CommentThread from "./CommentThread";
import CommentComposer from "./CommentComposer";

// Wraps the canonical BlockPreview renderer (same one the admin builder's
// Review step and the list's preview dialog use) with the reader-facing
// social layer — like + a per-block comment thread — so every content
// block, whatever its type, carries its own engagement independently.
//
// Knows nothing about a "Liked by" list itself — `renderLikeExtra(block)` is
// an optional render-prop slot right next to the like button, so a host
// (e.g. the admin preview dialog) can inject its own fully self-contained
// UI there (a moderator-only "who liked this" trigger) without this shared
// component, or LikeButton, needing to know that feature exists at all.
const InteractiveBlock = ({
  block,
  currentUser,
  canModerate,
  onToggleLike,
  onAddComment,
  onDeleteComment,
  onPinComment,
  renderLikeExtra,
}) => {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [newestFirst, setNewestFirst] = useState(true);
  const totalComments = block.comments?.total_comments || 0;

  // Pinned comments always stay in their own top group (most-recently-pinned
  // first, as the API already orders them) — only the unpinned rest respects
  // the Newest/Oldest toggle.
  const sortedComments = useMemo(() => {
    const items = block.comments?.items || [];
    const pinned = items.filter((c) => c.is_pinned);
    const unpinned = items
      .filter((c) => !c.is_pinned)
      .sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
    return [...pinned, ...(newestFirst ? unpinned.reverse() : unpinned)];
  }, [block.comments?.items, newestFirst]);

  // BlockPreview (the builder's renderer) expects the frontend block shape
  // ({ type: "paragraph"|"heading"|..., data }) — the API returns the raw
  // DB shape ({ type: "PARAGRAPH", content }), so translate before handing
  // it off, same mapping the admin builder's contentsToBlocks() uses.
  const previewBlock = { id: block.id, type: BACKEND_TYPE_TO_BLOCK[block.type] || "paragraph", data: block.content };
  const isHeading = previewBlock.type === "heading";

  if (isHeading) {
    return <BlockPreview block={previewBlock} />;
  }

  return (
    <div className="relative pb-7">
      <BlockPreview block={previewBlock} />

      {/* Absolutely positioned (anchored in the reserved bottom padding above)
          so it never shifts this block's own height — and therefore never
          shifts any block below it either. Always visible (no hover-reveal). */}
      <div className="absolute bottom-0 right-0 flex items-center gap-4 px-3 py-1.5 rounded-full bg-surface/95 backdrop-blur-md shadow-md">
        <LikeButton
          isLiked={block.viewer_state?.is_liked}
          totalLikes={block.likes_preview?.total_likes ?? block.engagement?.total_likes ?? 0}
          onToggle={() => onToggleLike(block.id)}
        />
        {renderLikeExtra?.(block)}

        <button
          type="button"
          onClick={() => setCommentsOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
            commentsOpen ? "text-primary" : "text-text_muted"
          }`}
        >
          <MessageCircle size={15} className={commentsOpen ? "fill-current" : ""} />
          {totalComments > 0 && totalComments}
        </button>
      </div>

      {commentsOpen && (
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h4 className="text-[14px] font-bold text-text_primary">Discussion</h4>
              <span className="w-5 h-5 rounded-full bg-surface_alt text-text_secondary text-[10.5px] font-semibold flex items-center justify-center">
                {totalComments}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {sortedComments.length > 1 && (
                <button
                  type="button"
                  onClick={() => setNewestFirst((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-text_muted"
                >
                  <ArrowUpDown size={11} />
                  {newestFirst ? "Newest first" : "Oldest first"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setCommentsOpen(false)}
                title="Collapse comments"
                className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-border text-text_muted"
              >
                <ChevronUp size={13} />
              </button>
            </div>
          </div>

          <CommentComposer currentUser={currentUser} onSubmit={(text) => onAddComment(block.id, text, null)} />

          {sortedComments.length > 0 && (
            <div className="mt-3 space-y-4">
              {sortedComments.map((comment) => (
                <CommentThread
                  key={comment.id}
                  comment={comment}
                  currentUser={currentUser}
                  canModerate={canModerate}
                  onReply={(parentId, text) => onAddComment(block.id, text, parentId)}
                  onDelete={onDeleteComment}
                  onPin={onPinComment}
                />
              ))}
            </div>
          )}

          {/* Mirrors the collapse control up in the header — once someone's
              scrolled down through a long thread, the toggle pill at the
              bottom of the block is long gone; this stays reachable right
              where they end up. */}
          {sortedComments.length > 3 && (
            <button
              type="button"
              onClick={() => setCommentsOpen(false)}
              className="w-full inline-flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-border text-[11.5px] font-semibold text-text_muted"
            >
              <ChevronUp size={13} />
              Collapse comments
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InteractiveBlock;
